/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { toast } from "react-toastify";
import ApiClient from "../../apis/apiClient";
import {
  API_BlogById,
  API_BlogCategories,
  API_BlogCreate,
  API_BlogImageUpload,
  API_BlogUpdate,
} from "../../apis/constant";
import BlogContentPreview from "./BlogContentPreview";
import BlogRichTextEditor from "./BlogRichTextEditor";
import { getBlogCategoryValueFromBlog } from "./blogCategoryOptions";
import { blogPrefillValue } from "./blogPrefillValue";

const FILE_URL = process.env.REACT_APP_FILE_URL;

const emptyForm = {
  title: "",
  slug: "",
  category: "",
  excerpt: "",
  coverAlt: "",
  tags: "",
  seoTitle: "",
  seoDescription: "",
  status: "0",
  contentHtml: "",
  contentJson: "",
};

const slugify = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getBlogId = (blog) => blog?._id || blog?.id;

const normalizeBlog = (blog) => {
  if (!blog) return emptyForm;

  return {
    title: blog.title || "",
    slug: blog.slug || "",
    category: getBlogCategoryValueFromBlog(blog),
    excerpt: blog.excerpt || "",
    coverAlt: blog.coverAlt || "",
    tags: Array.isArray(blog.tags) ? blog.tags.join(", ") : blog.tags || "",
    seoTitle: blog.seoTitle || "",
    seoDescription: blog.seoDescription || "",
    status: String(blog.status ?? 0),
    contentHtml: blog.contentHtml || "",
    contentJson:
      typeof blog.contentJson === "string"
        ? blog.contentJson
        : blog.contentJson
          ? JSON.stringify(blog.contentJson)
          : "",
  };
};

const appendFormValue = (formData, key, value) => {
  if (value !== undefined && value !== null) {
    formData.append(key, value);
  }
};

const BlogForm = ({ blogId, mode }) => {
  const adminToken = localStorage.getItem("wed_admin_token");
  const history = useHistory();
  const isEdit = mode === "edit";
  // const [form, setForm] = useState(() =>
  //   isEdit ? emptyForm : { ...emptyForm, ...blogPrefillValue },
  // );
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentCoverImage, setCurrentCoverImage] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const previewUrl = useMemo(() => {
    if (selectedFile) {
      return URL.createObjectURL(selectedFile);
    }

    return currentCoverImage ? `${FILE_URL}${currentCoverImage}` : "";
  }, [currentCoverImage, selectedFile]);

  useEffect(() => {
    return () => {
      if (selectedFile) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, selectedFile]);

  useEffect(() => {
    const getCategoryOptions = async () => {
      if (!adminToken) return;

      try {
        const res = await ApiClient.get(
          API_BlogCategories,
          "",
          `Bearer ${adminToken}`,
        );
        const backendCategories = Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        setCategoryOptions(backendCategories);
      } catch (error) {
        setCategoryOptions([]);
      }
    };

    getCategoryOptions();
  }, [adminToken]);

  useEffect(() => {
    const getBlog = async () => {
      if (!blogId) return;

      setLoading(true);
      const res = await ApiClient.get(
        API_BlogById + blogId,
        "",
        `Bearer ${adminToken}`,
      );

      if (res?.data?.code === 200) {
        const blog = res.data.data;
        setForm(normalizeBlog(blog));
        setCurrentCoverImage(blog?.coverImage || "");
      } else {
        toast.error(res?.data?.message || "Unable to fetch blog post");
      }
      setLoading(false);
    };

    getBlog();
  }, [adminToken, blogId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }));
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    updateField("slug", slugify(value));
  };

  const buildFormData = () => {
    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      appendFormValue(formData, key, form[key]);
    });

    if (selectedFile) {
      formData.append("coverImage", selectedFile);
    }

    return formData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setIsSubmit(true);
    const res = await ApiClient.multipart(
      isEdit ? "patch" : "post",
      isEdit ? API_BlogUpdate + blogId : API_BlogCreate,
      buildFormData(),
      `Bearer ${adminToken}`,
    );

    if (res?.data?.code === 200) {
      toast.success(res.data.message || "Blog post saved successfully");
      history.push("/blog");
    } else {
      toast.error(res?.data?.message || "Unable to save blog post");
      setIsSubmit(false);
    }
  };

  const uploadEditorImage = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append("image", file);

      const res = await ApiClient.multipart(
        "post",
        API_BlogImageUpload,
        formData,
        `Bearer ${adminToken}`,
      );

      if (res?.data?.code !== 200 || !res?.data?.data?.url) {
        throw new Error(res?.data?.message || "Unable to upload image");
      }

      return res.data.data.url;
    },
    [adminToken],
  );

  if (adminToken === null) {
    return <Redirect to="/" />;
  }

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="p-4">
      <div className="flex flex-wrap -mx-2">
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>Title:</label>
          <input
            type="text"
            className="form-input w-full"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Enter title"
          />
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>Slug:</label>
          <input
            type="text"
            className="form-input w-full"
            value={form.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="Auto-generated from title"
          />
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>Category:</label>
          <select
            className="form-select w-full"
            value={form.category}
            onChange={(e) => updateField("category", e.target.value)}
          >
            <option value="">Select category</option>
            {categoryOptions.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>Status:</label>
          <select
            className="form-select w-full"
            value={form.status}
            onChange={(e) => updateField("status", e.target.value)}
          >
            <option value="0">Draft</option>
            <option value="1">Published</option>
          </select>
        </div>
        <div className="w-full px-2 mb-4">
          <label>Summary:</label>
          <textarea
            className="form-input w-full"
            rows="4"
            value={form.excerpt}
            onChange={(e) => updateField("excerpt", e.target.value)}
            placeholder="Short summary"
          />
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>Cover Image:</label>
          <input
            type="file"
            accept="image/*"
            className="form-input w-full"
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>Cover Alt:</label>
          <input
            type="text"
            className="form-input w-full"
            value={form.coverAlt}
            onChange={(e) => updateField("coverAlt", e.target.value)}
            placeholder="Image alt text"
          />
        </div>
        {previewUrl && (
          <div className="w-full px-2 mb-4">
            <img
              src={previewUrl}
              alt={form.coverAlt || form.title}
              style={{ width: 180, height: 110, objectFit: "cover" }}
            />
          </div>
        )}
        <div className="w-full px-2 mb-4">
          <label>Tags:</label>
          <input
            type="text"
            className="form-input w-full"
            value={form.tags}
            onChange={(e) => updateField("tags", e.target.value)}
            placeholder="wedding, planning, decor"
          />
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>SEO Title:</label>
          <input
            type="text"
            className="form-input w-full"
            value={form.seoTitle}
            onChange={(e) => updateField("seoTitle", e.target.value)}
            placeholder="SEO title"
          />
        </div>
        <div className="w-full lg:w-1/2 px-2 mb-4">
          <label>SEO Description:</label>
          <textarea
            className="form-input w-full"
            rows="3"
            value={form.seoDescription}
            onChange={(e) => updateField("seoDescription", e.target.value)}
            placeholder="SEO description"
          />
        </div>
        <div className="w-full px-2 mb-4">
          <label>Content:</label>
          <BlogRichTextEditor
            value={{
              contentHtml: form.contentHtml,
              contentJson: form.contentJson,
            }}
            onImageUpload={uploadEditorImage}
            onChange={({ contentHtml, contentJson }) =>
              setForm((prev) => ({ ...prev, contentHtml, contentJson }))
            }
          />
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={() => setIsPreviewOpen(true)}
          className="btn btn-default btn-rounded bg-white hover:bg-grey-100 text-grey-900 border border-grey-200"
        >
          Preview
        </button>
        <Link
          to="/blog"
          className="btn btn-default btn-rounded bg-white hover:bg-grey-100 text-grey-900"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={isSubmit}
          className="btn btn-default bg-blue-500 hover:bg-blue-600 text-white btn-rounded"
        >
          {isSubmit ? "Saving..." : "Save"}
        </button>
      </div>
      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fullScreen
      >
        <DialogTitle>Blog Preview</DialogTitle>
        <DialogContent
          className="blog-preview-dialog-content"
          dividers
          sx={{ backgroundColor: "#f8fafc", padding: 0, scrollBehavior: "smooth" }}
        >
          <BlogContentPreview
            blog={form}
            coverImage={previewUrl}
            categoryOptions={categoryOptions}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <button
            type="button"
            onClick={() => setIsPreviewOpen(false)}
            className="btn btn-default btn-rounded bg-white hover:bg-grey-100 text-grey-900 border border-grey-200"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export { getBlogId };
export default BlogForm;
